// ============================================
// CreateGroupChatModal Component
// Modal for creating new group chats with member selection
// ============================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/lib/supabase/client';
import { createGroupChat, uploadGroupChatAvatar } from '@/lib/supabase/chat';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Camera, Loader2, Trash2, Users, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAvatarInitials } from '@/lib/utils';

const createGroupSchema = z.object({
  name: z.string()
    .min(1, 'El nombre del grupo es requerido')
    .max(100, 'El nombre no puede exceder 100 caracteres')
    .refine(name => name.trim().length > 0, 'El nombre no puede estar vacío'),
});

type CreateGroupForm = z.infer<typeof createGroupSchema>;

interface User {
  id: string;
  username: string;
  display_name?: string;
  avatar_url?: string;
}

interface CreateGroupChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGroupCreated?: (groupId: string, groupName: string, avatarUrl?: string) => void;
}

export const CreateGroupChatModal: React.FC<CreateGroupChatModalProps> = ({
  isOpen,
  onClose,
  onGroupCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [groupImageFile, setGroupImageFile] = useState<File | null>(null);
  const [groupImagePreviewUrl, setGroupImagePreviewUrl] = useState<string | null>(null);
  const groupImageInputRef = useRef<HTMLInputElement | null>(null);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateGroupForm>({
    resolver: zodResolver(createGroupSchema),
  });

  // Define handleClose before using it in useEffect
  const handleClose = useCallback(() => {
    reset();
    setSelectedUsers(new Set());
    setSearchQuery('');
    setGroupImageFile(null);
    if (groupImagePreviewUrl) {
      URL.revokeObjectURL(groupImagePreviewUrl);
    }
    setGroupImagePreviewUrl(null);
    onClose();
  }, [groupImagePreviewUrl, reset, onClose]);

  useEffect(() => {
    return () => {
      if (groupImagePreviewUrl) {
        URL.revokeObjectURL(groupImagePreviewUrl);
      }
    };
  }, [groupImagePreviewUrl]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  // Load users (followers/following) for member selection
  const loadUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);

      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('[CreateGroupChatModal] Auth error:', authError);
        toast({
          title: 'Error de autenticación',
          description: 'No se pudo verificar tu sesión.',
          variant: 'destructive',
        });
        return;
      }

      if (!user) {
        toast({
          title: 'No autenticado',
          description: 'Debes iniciar sesión para crear grupos.',
          variant: 'destructive',
        });
        return;
      }


      // Get users that current user follows (publishers they subscribe to)
      const { data: following, error: followingError } = await supabase
        .from('user_subscriptions')
        .select('publisher_id')
        .eq('follower_id', user.id);


      if (followingError) {
        console.error('[CreateGroupChatModal] Error loading following:', followingError);
      }

      // Get users that follow current user (followers)
      const { data: followers, error: followersError } = await supabase
        .from('user_subscriptions')
        .select('follower_id')
        .eq('publisher_id', user.id);


      if (followersError) {
        console.error('[CreateGroupChatModal] Error loading followers:', followersError);
      }

      // Combine user IDs
      const userIds = new Set<string>();

      following?.forEach((f: any) => {
        if (f.publisher_id && f.publisher_id !== user.id) {
          userIds.add(f.publisher_id);
        }
      });

      followers?.forEach((f: any) => {
        if (f.follower_id && f.follower_id !== user.id) {
          userIds.add(f.follower_id);
        }
      });


      if (userIds.size === 0) {

        // Fallback: load some recent users (excluding current user)
        const { data: recentUsers, error: recentError } = await supabase
          .from('profiles')
          .select('id, username, display_name, avatar_url')
          .neq('id', user.id)
          .limit(20);


        if (recentError) {
          console.error('[CreateGroupChatModal] Error loading recent users:', recentError);
          toast({
            title: 'Error al cargar usuarios',
            description: 'No se pudieron cargar usuarios disponibles.',
            variant: 'destructive',
          });
        } else {
          setUsers(recentUsers || []);
        }
        return;
      }

      // Get user profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .in('id', Array.from(userIds))
        .limit(100);


      if (profilesError) {
        console.error('[CreateGroupChatModal] Error loading profiles:', profilesError);
        toast({
          title: 'Error al cargar usuarios',
          description: 'No se pudieron cargar los perfiles de usuario.',
          variant: 'destructive',
        });
        return;
      }

      setUsers(profiles || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast({
        title: 'Error al cargar usuarios',
        description: 'No se pudieron cargar los usuarios disponibles.',
        variant: 'destructive',
      });
    } finally {
      setLoadingUsers(false);
    }
  }, [toast]);

  // Load users when modal opens
  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const filteredUsers = useMemo(() => users.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.display_name && user.display_name.toLowerCase().includes(searchQuery.toLowerCase()))
  ), [users, searchQuery]);

  const handleUserToggle = useCallback((userId: string) => {
    setSelectedUsers(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(userId)) {
        newSelected.delete(userId);
      } else {
        newSelected.add(userId);
      }
      return newSelected;
    });
  }, []);

  const handleGroupImageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Archivo no valido',
        description: 'Selecciona una imagen para el grupo.',
        variant: 'destructive',
      });
      return;
    }

    const maxFileSizeBytes = 8 * 1024 * 1024;
    if (file.size > maxFileSizeBytes) {
      toast({
        title: 'Imagen muy pesada',
        description: 'La imagen no puede superar 8 MB.',
        variant: 'destructive',
      });
      return;
    }

    if (groupImagePreviewUrl) {
      URL.revokeObjectURL(groupImagePreviewUrl);
    }

    const previewUrl = URL.createObjectURL(file);
    setGroupImageFile(file);
    setGroupImagePreviewUrl(previewUrl);

    // Permite volver a elegir el mismo archivo en intentos consecutivos.
    event.target.value = '';
  }, [groupImagePreviewUrl, toast]);

  const clearSelectedGroupImage = useCallback(() => {
    setGroupImageFile(null);
    if (groupImagePreviewUrl) {
      URL.revokeObjectURL(groupImagePreviewUrl);
    }
    setGroupImagePreviewUrl(null);
  }, [groupImagePreviewUrl]);

  const onSubmit = async (data: CreateGroupForm) => {
    if (selectedUsers.size === 0) {
      toast({
        title: 'Selecciona miembros',
        description: 'Debes seleccionar al menos un miembro para el grupo.',
        variant: 'destructive',
      });
      return;
    }

    if (selectedUsers.size > 50) {
      toast({
        title: 'Demasiados miembros',
        description: 'Un grupo no puede tener más de 50 miembros.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      let avatarUrl: string | undefined;
      if (groupImageFile) {
        avatarUrl = await uploadGroupChatAvatar(groupImageFile);
      }

      const groupId = await createGroupChat({
        name: data.name.trim(),
        member_ids: Array.from(selectedUsers),
        avatar_url: avatarUrl,
      });

      toast({
        title: 'Grupo creado',
        description: `El grupo "${data.name.trim()}" ha sido creado exitosamente.`,
      });

      reset();
      setSelectedUsers(new Set());
      setSearchQuery('');
      clearSelectedGroupImage();
      onClose();
      onGroupCreated?.(groupId, data.name.trim(), avatarUrl);
    } catch (error: any) {
      console.error('Error creating group:', error);
      toast({
        title: 'Error al crear grupo',
        description: error.message || 'Ocurrió un error al crear el grupo.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={(e) => {
        // Only close if clicking the backdrop directly
        if (e.target === e.currentTarget) {
          // Don't close - user must use buttons
        }
      }}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={(e) => e.stopPropagation()} />
      
      {/* Modal Content */}
      <div 
        className="relative bg-background rounded-lg shadow-lg border w-full max-w-[520px] max-h-[85vh] flex flex-col m-4 z-50"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
            <Users className="w-5 h-5 text-primary" />
          </div>
          <h2 className="font-semibold text-lg">Crear nuevo grupo</h2>
        </div>

        <form 
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(onSubmit)(e);
          }} 
          onClick={(e) => e.stopPropagation()}
          className="flex flex-col flex-1 min-h-0"
        >
          <div className="space-y-5 flex-1 overflow-hidden px-5 py-4" onClick={(e) => e.stopPropagation()}>
            {/* Group Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">Nombre del grupo *</Label>
              <Input
                id="name"
                placeholder="Ej: Amigos de la universidad"
                {...register('name')}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                className={cn(errors.name && 'border-destructive')}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            {/* Group Image */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Imagen del grupo</Label>
              <div className="flex items-center gap-3 rounded-lg border p-3 bg-muted/20">
                <Avatar className="w-14 h-14">
                  <AvatarImage src={groupImagePreviewUrl || undefined} />
                  <AvatarFallback>
                    <Users className="w-5 h-5" />
                  </AvatarFallback>
                </Avatar>

                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={groupImageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleGroupImageChange}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      groupImageInputRef.current?.click();
                    }}
                  >
                    <Camera className="w-4 h-4 mr-1.5" />
                    {groupImageFile ? 'Cambiar imagen' : 'Elegir imagen'}
                  </Button>
                  {groupImageFile && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearSelectedGroupImage();
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" />
                      Quitar
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground w-full">PNG, JPG o WEBP. Maximo 8 MB.</p>
                </div>
              </div>
            </div>

            {/* Member Selection */}
            <div className="space-y-3 flex-1 min-h-0">
              <div className="flex items-center justify-between">
                <Label>Miembros ({selectedUsers.size} seleccionados)</Label>
                {selectedUsers.size > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedUsers(new Set());
                    }}
                    className="h-auto p-1 text-xs"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Limpiar
                  </Button>
                )}
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar usuarios..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  className="pl-10 h-10"
                />
              </div>

              {/* Selected Members Preview */}
              {selectedUsers.size > 0 && (
                <div className="flex flex-wrap gap-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                  {Array.from(selectedUsers).map(userId => {
                    const user = users.find(u => u.id === userId);
                    return user ? (
                      <div 
                        key={userId} 
                        className="flex items-center gap-1.5 bg-primary text-primary-foreground rounded-full px-3 py-1.5 text-xs font-medium shadow-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Avatar className="w-4 h-4">
                          <AvatarImage src={user.avatar_url} />
                          <AvatarFallback className="text-xs">
                            {getAvatarInitials(user.display_name || user.username)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate max-w-20 font-medium">{user.display_name || user.username}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUserToggle(userId);
                          }}
                          className="h-auto p-0 ml-1 hover:bg-primary-foreground/20"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : null;
                  })}
                </div>
              )}

              {/* User List */}
              <ScrollArea 
                className="flex-1 border rounded-lg p-2 max-h-[240px]"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="ml-2">Cargando usuarios...</span>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No se encontraron usuarios</p>
                    <p className="text-sm">Sigue a más usuarios para crear grupos</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        loadUsers();
                      }}
                      className="mt-2"
                    >
                      Recargar
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        className={cn(
                          'flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all',
                          selectedUsers.has(user.id) 
                            ? 'bg-primary/10 border-2 border-primary shadow-sm' 
                            : 'hover:bg-muted/70 border-2 border-transparent'
                        )}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onPointerDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleUserToggle(user.id);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleUserToggle(user.id);
                          }
                        }}
                      >
                        <div className={cn(
                          'w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0',
                          selectedUsers.has(user.id)
                            ? 'bg-primary border-primary scale-110'
                            : 'border-muted-foreground/30 bg-background'
                        )}>
                          {selectedUsers.has(user.id) && (
                            <svg
                              className="w-3.5 h-3.5 text-primary-foreground"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="3"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <Avatar className="w-10 h-10 flex-shrink-0">
                          <AvatarImage src={user.avatar_url} />
                          <AvatarFallback>
                            {getAvatarInitials(user.display_name || user.username)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            "font-medium text-sm truncate",
                            selectedUsers.has(user.id) && "text-primary"
                          )}>
                            {user.display_name || user.username}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            @{user.username}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Helper Text */}
              <div className="flex items-center justify-between text-xs" onClick={(e) => e.stopPropagation()}>
                <p className="text-muted-foreground">
                  Seleccionados: <span className="font-semibold text-primary">{selectedUsers.size}</span> {selectedUsers.size === 1 ? 'miembro' : 'miembros'}
                </p>
                {selectedUsers.size > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedUsers(new Set());
                    }}
                  >
                    Limpiar todo
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t bg-muted/20" onClick={(e) => e.stopPropagation()}>
            <Button 
              type="button" 
              variant="outline" 
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }} 
              disabled={loading}
              className="min-w-[100px]"
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={loading || selectedUsers.size === 0}
              onClick={(e) => e.stopPropagation()}
              className="min-w-[140px]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Users className="w-4 h-4 mr-2" />
                  Crear grupo ({selectedUsers.size})
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};